import { format, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';

export function formatMoney(amount: number): string {
  return `₪${amount.toLocaleString('en-US')}`;
}

export function formatIsraeliPhoneNumber(
  phoneNumber: string | undefined,
): string {
  if (!phoneNumber) {
    return '';
  }

  let cleanedNumber = phoneNumber.replace(/[^0-9]/g, '');

  if (cleanedNumber.startsWith('0')) {
    cleanedNumber = cleanedNumber.substring(1);
  }
  if (cleanedNumber.startsWith('972')) {
    return `+${cleanedNumber}`;
  }

  return `+972${cleanedNumber}`;
}

export function formatStatus(status: string): string {
  if (status === 'REJECTED') {
    return 'נדחה';
  }
  if (status === 'IN_PROGRESS') {
    return 'בתהליך';
  }
  if (status === 'COMPLETED') {
    return 'נקלט';
  }
  return 'error';
}

export function formatDate(dateString: string): string {
  const monthsInHebrew = [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
  ];

  const [year, month, day] = dateString.split('-');

  const monthName = monthsInHebrew[parseInt(month, 10) - 1];

  return `${year} ${monthName}`;
}

export const formatDateNumeral = (dateString: string) => {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  return `${day}.${month}.${year}`;
};

export const calculateAge = (birthDateString: string) => {
  const year = parseInt(birthDateString.substring(0, 4), 10);
  const month = parseInt(birthDateString.substring(4, 6), 10) - 1;
  const day = parseInt(birthDateString.substring(6, 8), 10);

  const today = new Date();
  const birthDate = new Date(year, month, day);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

export const formatSalaryMonth = (date: string | null | undefined): string => {
  if (!date) {
    return '';
  }
  try {
    const parsedDate = parseISO(date);

    return format(parsedDate, 'LLLL yyyy', { locale: he });
  } catch (error) {
    console.error(`Error: ${date}`, error);
    return '';
  }
};

export const formatFileDate = (rawDate: string): string | null => {
  if (!rawDate || rawDate.length < 8) return null;

  const year = parseInt(rawDate.substring(0, 4), 10);
  const month = parseInt(rawDate.substring(4, 6), 10);
  const day = parseInt(rawDate.substring(6, 8), 10);

  const date = new Date(year, month - 1, day);

  date.setMonth(date.getMonth() - 1);

  const adjustedYear = date.getFullYear();
  const adjustedMonth = String(date.getMonth() + 1).padStart(2, '0');
  const adjustedDay = String(date.getDate()).padStart(2, '0');

  return `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
};

export const formatFundType = (fundType: string): string => {
  switch (fundType) {
    case '1':
      return 'קופת ביטוח';
    case '2':
      return 'קרן פנסיה';
    case '3':
      return 'קופת גמל';
    case '4':
      return 'קרן השתלמות';
    default:
      return 'סוג קופה לא ידוע';
  }
};

export const extractFundNumber = (input: string | null | undefined): number => {
  if (!input || typeof input !== 'string') {
    return 0;
  }

  if (/^0{5}0{7}$/.test(input)) {
    return 0;
  }

  const match = input.match(/0+(\d{1,9})0{7}$/);

  if (match) {
    return parseInt(match[1], 10);
  }

  return 0;
};

export const extractCompanyId = (input: string | null | undefined): string => {
  if (!input || typeof input !== 'string' || input.length < 9) {
    return 'ERROR';
  }

  return input.substring(0, 9);
};

export const extractIdentifier = (input: string | null | undefined): string => {
  if (!input || typeof input !== 'string') {
    return '-';
  }

  // Проверяем, является ли это fund identifier (паттерн с нулями в конце)
  if (/^0{5}0{7}$/.test(input)) {
    return '0';
  }

  const fundMatch = input.match(/0+(\d{1,9})0{7}$/);
  if (fundMatch) {
    return fundMatch[1];
  }

  // Если не fund identifier, то пытаемся извлечь company ID
  if (input.length >= 9) {
    return input.substring(0, 9);
  }

  return input;
};

export const extractGeneralizedFundType = (
  fundType: string,
  fundNumber: string | null | undefined,
): string => {
  const extractedNum = extractFundNumber(fundNumber); // Ваша функция extractFundNumber

  if (extractedNum === 0) {
    return 'ביטוח מנהלים';
  } else {
    switch (fundType) {
      case '1':
        return 'קופת ביטוח';
      case '2':
        return 'קרן פנסיה';
      case '3':
        return 'קופת גמל';
      case '4':
        return 'קרן השתלמות';
      default:
        return 'סוג קופה לא ידוע';
    }
  }
};
